import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, AlertColor, Slide, SlideProps } from '@mui/material';

/* Slide-up transition */
const SlideUp = (props: SlideProps) => <Slide {...props} direction="up" />;

interface ToastContextType {
  showToast: (message: string, severity?: AlertColor) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('success');

  const showToast = useCallback((msg: string, sev: AlertColor = 'success') => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  }, []);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={SlideUp}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="standard"
          sx={{
            width: '100%',
            borderRadius: 2,
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            ...(severity === 'success' && {
              bgcolor: '#f0faf0',
              color: '#2e6e3e',
              '& .MuiAlert-icon': { color: '#3a9e55' },
            }),
            ...(severity === 'error' && {
              bgcolor: '#fef2f2',
              color: '#8b3a3a',
              '& .MuiAlert-icon': { color: '#c45454' },
            }),
            ...(severity === 'warning' && {
              bgcolor: '#fefce8',
              color: '#7c6a1e',
              '& .MuiAlert-icon': { color: '#b8981a' },
            }),
            ...(severity === 'info' && {
              bgcolor: '#eff6ff',
              color: '#3565a0',
              '& .MuiAlert-icon': { color: '#4a8ad4' },
            }),
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};
